import { createContext, useContext, useEffect, useState } from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check if user has a preference in localStorage
    const stored = localStorage.getItem('theme') as Theme | null;
    if (stored) return stored;

    // Default to dark theme for Recop (design is dark-first)
    return 'dark';
  });

  useEffect(() => {
    // Update document data attribute for Panda CSS
    document.documentElement.setAttribute('data-color-mode', theme);

    // Store preference
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <SkeletonTheme baseColor="#1e1f24" highlightColor="#16171b">
        {children}
      </SkeletonTheme>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
